import type { PieSeriesOption } from 'echarts';
import type { PieChartConfig, PieChartDatum } from './types';

// Single donut series. Mirrors the amcharts SimplePieChart look: 38% inner radius, a 1px paper-color
// divider stroke between slices, labels/ticks disabled (the React legend replaces them), and a soft
// drop shadow on hover (amcharts hoverShadow opacity 0.4 / blur 5). Slices do not scale on hover.
export const buildPieSeries = (data: PieChartDatum[], config: PieChartConfig): PieSeriesOption => {
  const {
    theme,
    innerRadiusPercent = 38,
    outerRadiusPercent = 90,
    strokeColor,
    strokeWidth = 1,
    hoverShadow = true,
    interactive = true,
  } = config;

  return {
    type: 'pie',
    radius: [`${innerRadiusPercent}%`, `${outerRadiusPercent}%`],
    center: ['50%', '50%'],
    silent: !interactive,
    avoidLabelOverlap: false,
    label: { show: false },
    labelLine: { show: false },
    // amcharts only added a shadow on hover — it did not grow the slice — so disable ECharts'
    // default emphasis scale and keep just the shadow when this chart supports hover feedback.
    emphasis:
      interactive && hoverShadow
        ? { scale: false, itemStyle: { shadowBlur: 5, shadowColor: 'rgba(0, 0, 0, 0.4)' } }
        : { disabled: true },
    itemStyle: {
      borderColor: strokeColor ?? theme.bgColor,
      borderWidth: strokeWidth,
    },
    data: data.map((d) => ({
      // Explicit identity prevents ECharts from reconciling a reordered report by index, which
      // can leave an outgoing sector visually layered over a different incoming category.
      id: d.name,
      name: d.name,
      value: d.value,
      itemStyle: { color: d.color },
    })),
  };
};
